import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { sanitizeInput } from '../../utils/sanitizer';
import { logger } from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';

const VoiceInputButton = ({ value, onChange, maxLength = 5000, className = '' }) => {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState('');
  const { userProfile } = useAuth();

  // Holds the current active recognition instance
  const recognitionRef = useRef(null);
  // Snapshot of text that existed before this voice session started
  const baseTextRef = useRef('');
  // Accumulates ONLY finalized transcript text during this session
  const sessionFinalTextRef = useRef('');
  // The highest result index we have already finalized — prevents double-processing
  const lastFinalizedIndexRef = useRef(-1);
  // Session ID — incremented each time a new session starts.
  // Events from a stale session (different ID) are silently discarded.
  const sessionIdRef = useRef(0);
  // Whether we intentionally stopped (vs browser auto-stopping)
  const intentionalStopRef = useRef(false);
  // Whether we want to keep listening (for restart loop)
  const keepListeningRef = useRef(false);

  // Cleanup: abort any active recognition when the component unmounts
  useEffect(() => {
    return () => {
      keepListeningRef.current = false;
      intentionalStopRef.current = true;
      if (recognitionRef?.current) {
        try {
          recognitionRef.current.onresult = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onend = null;
          recognitionRef?.current?.abort();
        } catch (e) {
          // Ignore abort errors on unmount
        }
        recognitionRef.current = null;
      }
    };
  }, []);

  const startRecognitionInstance = useCallback((capturedSessionId) => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      return;
    }

    // Guard: if session changed or we no longer want to listen, bail out
    if (capturedSessionId !== sessionIdRef?.current || !keepListeningRef?.current) {
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    // Use continuous=false to avoid the browser accumulating results across restarts.
    // We manually restart in onend to simulate continuous listening.
    const instance = new SpeechRecognition();
    instance.continuous = false;
    instance.interimResults = true;
    instance.lang = userProfile?.language_preference || 'en-US';
    instance.maxAlternatives = 1;

    instance.onresult = (event) => {
      // Discard events from a stale session
      if (capturedSessionId !== sessionIdRef?.current) return;

      const results = event?.results;
      if (!results) return;

      for (let i = event?.resultIndex ?? 0; i < results?.length; i++) {
        if (results?.[i]?.isFinal && i > lastFinalizedIndexRef?.current) {
          sessionFinalTextRef.current += results?.[i]?.[0]?.transcript || '';
          lastFinalizedIndexRef.current = i;
        }
      }

      // Get current interim result (last non-final result, if any)
      let interimTranscript = '';
      const lastResult = results?.[results?.length - 1];
      if (lastResult && !lastResult?.isFinal) {
        interimTranscript = lastResult?.[0]?.transcript || '';
      }

      const displayText = baseTextRef?.current + sessionFinalTextRef?.current + interimTranscript;
      const sanitized = sanitizeInput(displayText?.trim(), { maxLength, allowHtml: false, removeControlChars: false });
      onChange(sanitized);
    };

    instance.onerror = (event) => {
      if (capturedSessionId !== sessionIdRef?.current) return;
      logger?.error('Speech recognition error', event?.error);
      if (event?.error === 'no-speech') {
        // no-speech is non-fatal — restart if still listening
        return;
      } else if (event?.error === 'not-allowed') {
        setError('Microphone access denied. Please allow microphone permissions.');
        keepListeningRef.current = false;
        setIsListening(false);
      } else if (event?.error === 'aborted') {
        // Intentional stop — not an error
        return;
      } else {
        setError('Voice recognition error. Please try again.');
        keepListeningRef.current = false;
        setIsListening(false);
      }
    };

    instance.onend = () => {
      if (capturedSessionId !== sessionIdRef?.current) return;
      recognitionRef.current = null;

      // Restart automatically if we still want to keep listening
      if (keepListeningRef?.current && !intentionalStopRef?.current) {
        // Reset result index for the new instance (fresh continuous=false session)
        lastFinalizedIndexRef.current = -1;
        // Small delay to avoid rapid restart loops
        setTimeout(() => {
          startRecognitionInstance(capturedSessionId);
        }, 100);
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = instance;
    try {
      instance?.start();
    } catch (e) {
      logger?.error('Failed to start speech recognition', e);
      setError('Could not start voice recognition. Please try again.');
      recognitionRef.current = null;
      keepListeningRef.current = false;
      setIsListening(false);
    }
  }, [userProfile, maxLength, onChange]);

  const handleVoiceToggle = () => {
    if (isListening) {
      // Stop current session
      keepListeningRef.current = false;
      intentionalStopRef.current = true;
      // Increment session ID so any in-flight events are discarded
      sessionIdRef.current += 1;
      if (recognitionRef?.current) {
        try {
          recognitionRef.current.onresult = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onend = null;
          recognitionRef?.current?.abort();
        } catch (e) {
          // Ignore
        }
        recognitionRef.current = null;
      }
      setIsListening(false);
      return;
    }

    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      setError('Voice recognition not supported in this browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    setError('');
    // Capture current text as the base before starting — add trailing space only if non-empty
    baseTextRef.current = value ? value?.trimEnd() + ' ' : '';
    // Reset session trackers — completely fresh every time
    sessionFinalTextRef.current = '';
    lastFinalizedIndexRef.current = -1;
    intentionalStopRef.current = false;
    keepListeningRef.current = true;

    // Increment session ID — this invalidates all events from any previous session
    sessionIdRef.current += 1;
    const newSessionId = sessionIdRef?.current;

    setIsListening(true);
    startRecognitionInstance(newSessionId);
  };

  return (
    <div className={className}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleVoiceToggle}
        className={isListening ? 'bg-error/10 border-error text-error' : ''}
        iconName={isListening ? 'MicOff' : 'Mic'}
      >
        {isListening ? 'Stop' : 'Voice'}
      </Button>
      
      {isListening && (
        <div className="mt-2 p-2 bg-error/5 border border-error/20 rounded-lg flex items-center gap-2">
          <div className="w-2 h-2 bg-error rounded-full animate-pulse"></div>
          <span className="text-xs text-error font-medium">Listening... Speak now</span>
        </div>
      )}
      
      {error && (
        <div className="mt-2 bg-error/10 border border-error/20 rounded-lg p-2 flex items-start gap-2">
          <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mt-0.5" />
          <p className="text-xs text-error">{error}</p>
        </div>
      )}
    </div>
  );
};

export default VoiceInputButton;